// STEP 2, `/condition` — THE APP'S "ANYTHING WE SHOULD KNOW?" (WEB-PARITY-3, 3A: CD-1 to CD-9).
//
// The app's screen (`PropertyConditionView`, AppealAce/Features/Reasons/PropertyConditionView.swift, and its two cards in
// CaseEvidenceCards.swift): the hero, the "Condition & damage" card with its chips and its worked example, the "Photo
// evidence" card with an 84-point thumbnail per photo, "Take a photo" and "Add from library", and the photo sheet — the
// caption, "Draft with AI", "Done". Every word is the app's (`parity3Wording.ts`, each pinned to its Swift line).
//
// WHAT THE SERVER OWNS AND THIS PAGE ONLY SENDS:
//   - the note and the photos go to `v2Value` `evidence`. THE WHOLE PHOTO LIST, EVERY TIME (CD-4, CD-7): a stored photo
//     by its id alone (kept exactly as stored), a new one with its bytes, a re-captioned one with its caption — so adding
//     one keeps the others, and leaving one out is the app's "Remove";
//   - "Draft with AI" is `captionAuthor: "drafted"` on the photo, and the server reads the stored bytes to draft from
//     (CD-6); what came of it is the photo's `captionDraft`, in the app's words;
//   - each thumbnail is the stored photo's bytes, read back with `v2Value` `photo` (CD-5).
// A photo is REDRAWN before it is sent — 1,600 px, JPEG 0.75, and no EXIF or GPS — as the app's `PhotoStorage` does
// (`app/photoPrep.ts`).
//
// THE PHOTO BLOCK (R-pending-web-photo-block, Tyler 2026-10-22 "3 yes"): Tyler's approved "Photos are your strongest evidence"
// block sits between the hero and the two cards (`photoBlock.tsx`, its words in `photoBlockWording.ts`) — the whole block for
// Douglas County, Nebraska (the subject's state and county on the snapshot this step's own call returns), and for every other
// county only the pieces that are not Douglas-specific. It is the website's own static words: no server call, no flag.
//
// WHAT IS NOT HERE ANY MORE (CD-8): the condition deduction card. The app shows the deduction on its Value step behind
// "Adjust for condition" (ValueView.swift:817-931), which is where Batch 4 puts it; and the explanatory-ground note,
// which the app shows there too.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ValueEvidenceInput, ValueResponse } from "@contract";
import { browserCodec, preparePhoto } from "../../app/photoPrep";
import { useAppealId, useSession } from "../../app/session";
import { DegradedList } from "../../components/copy";
import { PaidRoute } from "../../components/PaidRoute";
import { Refusal } from "../../components/Refusal";
import { Card, Said, Sheet, StepHero } from "./parity3Parts";
import { isUnitInBuilding, PHOTO_LIMIT, withChip } from "./parity3Rules";
import { say, sayWith, type Parity3WordingId } from "./parity3Wording";
import { PhotoBlock } from "./photoBlock";
import { isDouglasCountyNebraska } from "./photoBlockWording";

type Photo = ValueResponse["body"]["photos"][number];
type PhotoEntry = NonNullable<ValueEvidenceInput["photos"]>[number];

const HOUSE_CHIPS: readonly Parity3WordingId[] = [
  "condition.chip.house.roof", "condition.chip.house.foundation", "condition.chip.house.water", "condition.chip.house.mold",
  "condition.chip.house.kitchen", "condition.chip.house.hvac", "condition.chip.house.electrical",
  "condition.chip.house.renovation",
];
const UNIT_CHIPS: readonly Parity3WordingId[] = [
  "condition.chip.unit.kitchen", "condition.chip.unit.hvac", "condition.chip.unit.electrical", "condition.chip.unit.water",
  "condition.chip.unit.special", "condition.chip.unit.hoa", "condition.chip.unit.renovation",
];

export function Condition() {
  const session = useSession();
  const appealId = useAppealId();
  return (
    <PaidRoute<ValueResponse>
      // `evidence` with no fields reads the stored note and photos back and changes nothing (WEB-FLOW-ORDER).
      probe={() => session.transport.v2Value.evidence({ appealId })}
      deps={[session.transport, appealId, session.revision]}
    >
      {(response, appeal) => (
        <ConditionStep
          first={response}
          unitInBuilding={isUnitInBuilding(appeal.subject?.propertyType?.value)}
          douglasNebraska={isDouglasCountyNebraska(appeal.subject?.state, appeal.subject?.county)}
        />
      )}
    </PaidRoute>
  );
}

function ConditionStep({
  first, unitInBuilding, douglasNebraska,
}: { first: ValueResponse; unitInBuilding: boolean; douglasNebraska: boolean }) {
  const session = useSession();
  const appealId = useAppealId();
  const navigate = useNavigate();
  const [latest, setLatest] = useState<ValueResponse>(first);
  // The owner's own text as stored, so coming back to the step shows what they wrote (CD-3).
  const [note, setNote] = useState(first.body.condition.note);
  const savedNote = useRef<string>(first.body.condition.note);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Extract<Awaited<ReturnType<typeof session.transport.v2Value.evidence>>, { ok: false }> | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  /** Each photo's picture, by id: the bytes this page just sent, or the server's read-back. */
  const [pictures, setPictures] = useState<Readonly<Record<string, string>>>({});
  const asked = useRef(new Set<string>());

  const photos = latest.body.photos;

  // THE THUMBNAILS (CD-5): every stored photo this page has no picture for is read back once.
  useEffect(() => {
    for (const photo of photos) {
      if (pictures[photo.photoId] !== undefined || asked.current.has(photo.photoId)) continue;
      asked.current.add(photo.photoId);
      void session.transport.v2Value.photo({ appealId, photoId: photo.photoId }).then((result) => {
        if (!result.ok) return; // the frame stays empty, as the app's does when it cannot load the file (:180-182)
        const p = result.response.body.photo;
        setPictures((before) => ({ ...before, [p.photoId]: `data:${p.contentType};base64,${p.base64}` }));
      });
    }
  }, [photos, pictures, session.transport, appealId]);

  /** One `evidence` call; the response is the page's new state. */
  async function send(input: Omit<ValueEvidenceInput, "appealId">): Promise<ValueResponse | null> {
    setBusy(true);
    const result = await session.transport.v2Value.evidence({ appealId, ...input });
    setBusy(false);
    if (!result.ok) {
      setFailure(result);
      return null;
    }
    setFailure(null);
    setLatest(result.response);
    return result.response;
  }

  /** The stored list as the request names it: each by its id alone, so each is kept as it is. */
  const keepAll = (): PhotoEntry[] => photos.map((p) => ({ photoId: p.photoId }));

  /** The last note save still on its way, so Continue never leaves before it lands. */
  const noteSaving = useRef<Promise<unknown>>(Promise.resolve());
  async function saveNote(text: string) {
    if (text === savedNote.current || text.trim() === "") return noteSaving.current;
    savedNote.current = text;
    // THE NOTE ALONE (WEB-PARITY-4): the app's Condition step writes no ground — its Value step ticks the grounds on
    // arrival from what the owner has given (`seedReasonsIfNeeded`, ValueView.swift:1761-1807), the condition ground among
    // them where a note is on file. A ground sent from here filled the list before that seed could run, so Value opened
    // with condition alone and "Overvaluation" unticked.
    noteSaving.current = send({ conditionNote: text });
    return noteSaving.current;
  }

  async function addFiles(files: File[]) {
    const room = PHOTO_LIMIT - photos.length;
    const chosen = files.slice(0, Math.max(0, room));
    if (chosen.length === 0) return;
    const prepared = await Promise.all(chosen.map((file) => preparePhoto(file, browserCodec)));
    const before = new Set(photos.map((p) => p.photoId));
    const after = await send({
      photos: [...keepAll(), ...prepared.map((p) => ({ base64: p.base64, contentType: p.contentType, captionAuthor: "owner" as const }))],
    });
    if (after === null) return;
    const added = after.body.photos.filter((p) => !before.has(p.photoId));
    setPictures((was) => {
      const next = { ...was };
      added.forEach((p, i) => {
        if (prepared[i] !== undefined) next[p.photoId] = `data:${prepared[i].contentType};base64,${prepared[i].base64}`;
      });
      return next;
    });
    // Adding ONE photo goes straight into describing it (:339-340).
    if (added.length === 1) setEditing(added[0].photoId);
  }

  async function remove(photoId: string) {
    await send({ photos: photos.filter((p) => p.photoId !== photoId).map((p) => ({ photoId: p.photoId })) });
  }

  const chips = unitInBuilding ? UNIT_CHIPS : HOUSE_CHIPS;
  const editingPhoto = photos.find((p) => p.photoId === editing) ?? null;
  const coarse = useMemo(
    () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches,
    [],
  );

  return (
    <div className="condition-step">
      <StepHero icon="wrench" titleId="condition.hero.title" bodyId={unitInBuilding ? "condition.hero.unit" : "condition.hero.house"} />

      {/* Tyler's approved "Photos are your strongest evidence" block (2026-10-22, "3 yes"; photoBlockWording.ts), above both
          cards so the owner reads it before adding photos: the whole block for Douglas County, Nebraska, and only its
          pieces that are not Douglas-specific everywhere else. */}
      <PhotoBlock douglas={douglasNebraska} />

      <div className="condition-grid">
        <Card titleId="condition.card.title" data-card="condition" footer={<Said id="condition.card.footer" as="p" />}>
          <div className="chips" role="group">
            {chips.map((id) => (
              <button
                key={id}
                type="button"
                className="chip"
                data-held-copy={id}
                onClick={() => {
                  const next = withChip(note, say(id));
                  setNote(next);
                  void saveNote(next);
                }}
              >
                {say(id)}
              </button>
            ))}
          </div>
          <textarea
            className="condition-note"
            aria-label={say("condition.card.title")}
            placeholder={say(unitInBuilding ? "condition.placeholder.unit" : "condition.placeholder.house")}
            data-held-copy-placeholder={unitInBuilding ? "condition.placeholder.unit" : "condition.placeholder.house"}
            value={note}
            rows={5}
            onChange={(e) => setNote(e.currentTarget.value)}
            onBlur={() => void saveNote(note)}
          />
        </Card>

        <Card
          titleId="photos.title"
          data-card="photos"
          data-photo-count={photos.length}
          footer={<p data-held-copy="photos.footer">{sayWith("photos.footer", { exhibit: say("photos.exhibit") })}</p>}
        >
          {photos.length > 0 && (
            <ul className="thumbs">
              {photos.map((photo) => (
                <Thumb
                  key={photo.photoId}
                  photo={photo}
                  picture={pictures[photo.photoId] ?? null}
                  disabled={busy}
                  onOpen={() => setEditing(photo.photoId)}
                  onRemove={() => void remove(photo.photoId)}
                />
              ))}
            </ul>
          )}
          <div className="photo-buttons">
            {coarse && (
              <PickButton id="photos.take" capture disabled={busy || photos.length >= PHOTO_LIMIT} onFiles={(f) => void addFiles(f)} />
            )}
            <PickButton id="photos.library" disabled={busy || photos.length >= PHOTO_LIMIT} onFiles={(f) => void addFiles(f)} />
          </div>
        </Card>
      </div>

      {failure !== null && <Refusal failure={failure} context={say("photos.title")} />}
      <DegradedList degraded={latest.degraded} />

      <div className="step-footer centered">
        <Said id="condition.footnote" as="p" className="requirement" />
        <button
          className="btn primary"
          type="button"
          data-held-copy="condition.continue"
          onClick={async () => {
            // Leaving the field to press this saves the note too; either way the save lands before the next step.
            await saveNote(note);
            navigate("/comps");
          }}
        >
          {say("condition.continue")}
        </button>
      </div>

      {editingPhoto !== null && (
        <CaptionSheet
          photo={editingPhoto}
          picture={pictures[editingPhoto.photoId] ?? null}
          onClose={() => setEditing(null)}
          onSave={async (caption) => {
            if (caption === editingPhoto.caption) return;
            await send({
              photos: photos.map((p) => (p.photoId === editingPhoto.photoId
                ? { photoId: p.photoId, caption, captionAuthor: "owner" as const }
                : { photoId: p.photoId })),
            });
          }}
          onDraft={async (hint) => {
            const after = await send({
              photos: photos.map((p) => (p.photoId === editingPhoto.photoId
                ? { photoId: p.photoId, caption: hint, captionAuthor: "drafted" as const }
                : { photoId: p.photoId })),
            });
            return after?.body.photos.find((p) => p.photoId === editingPhoto.photoId) ?? null;
          }}
        />
      )}
    </div>
  );
}

/** One 84-point thumbnail (CaseEvidenceCards.swift:175-214): the picture, the amber mark and "Add description" while it
 *  has no caption, else the caption in two lines; and the app's "Remove" (:121-127), shown rather than long-pressed. */
function Thumb({
  photo, picture, disabled, onOpen, onRemove,
}: { photo: Photo; picture: string | null; disabled: boolean; onOpen: () => void; onRemove: () => void }) {
  const uncaptioned = photo.caption.trim() === "";
  return (
    <li className="thumb" data-photo-id={photo.photoId} data-caption-author={photo.captionAuthor}>
      <button type="button" className="thumb-open" onClick={onOpen} disabled={disabled}>
        <span className="thumb-frame">
          {picture === null ? <span className="thumb-empty" aria-hidden="true" /> : <img src={picture} alt="" />}
          {uncaptioned && <span className="thumb-mark" aria-hidden="true">!</span>}
        </span>
        {uncaptioned
          ? <Said id="photos.addDescription" className="thumb-badge" />
          : <span className="thumb-caption">{photo.caption}</span>}
      </button>
      <button type="button" className="thumb-remove" data-held-copy="photos.remove" disabled={disabled} onClick={onRemove}>
        {say("photos.remove")}
      </button>
    </li>
  );
}

/** "Take a photo" and "Add from library" (:134-146): a file picker each, the first asking for the camera. */
function PickButton({
  id, capture = false, disabled, onFiles,
}: { id: Parity3WordingId; capture?: boolean; disabled: boolean; onFiles: (files: File[]) => void }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <button type="button" className="btn photo-pick" data-held-copy={id} disabled={disabled} onClick={() => input.current?.click()}>
        {say(id)}
      </button>
      <input
        ref={input}
        type="file"
        hidden
        accept="image/jpeg,image/png"
        multiple={!capture}
        capture={capture ? "environment" : undefined}
        data-picker={id}
        onChange={(e) => {
          const files = [...(e.currentTarget.files ?? [])];
          e.currentTarget.value = "";
          if (files.length > 0) onFiles(files);
        }}
      />
    </>
  );
}

/** The photo sheet (CaseEvidenceCards.swift:216-273): the picture, the caption, "Draft with AI", the footer — or what
 *  became of the draft — and "Done", which keeps what was typed. */
function CaptionSheet({
  photo, picture, onClose, onSave, onDraft,
}: {
  photo: Photo;
  picture: string | null;
  onClose: () => void;
  onSave: (caption: string) => Promise<void>;
  onDraft: (hint: string) => Promise<Photo | null>;
}) {
  const [caption, setCaption] = useState(photo.caption);
  const [drafting, setDrafting] = useState(false);
  const [note, setNote] = useState<Parity3WordingId | null>(null);
  const [error, setError] = useState<Parity3WordingId | null>(null);
  return (
    <Sheet
      titleId="photos.sheet.title"
      kind="photo"
      confirm={{
        id: "photos.sheet.done",
        disabled: drafting,
        onPress: async () => {
          await onSave(caption);
          onClose();
        },
      }}
    >
      {picture !== null && <img className="sheet-photo" src={picture} alt="" />}
      <label className="sheet-field">
        <Said id="photos.sheet.header" className="sheet-header" />
        <textarea
          value={caption}
          rows={4}
          maxLength={300}
          placeholder={say("photos.sheet.placeholder")}
          data-held-copy-placeholder="photos.sheet.placeholder"
          onChange={(e) => setCaption(e.currentTarget.value)}
        />
      </label>
      <button
        type="button"
        className="btn quiet draft"
        data-held-copy={drafting ? "photos.sheet.drafting" : "photos.sheet.draft"}
        disabled={drafting}
        onClick={async () => {
          setNote(null);
          setError(null);
          setDrafting(true);
          const after = await onDraft(caption);
          setDrafting(false);
          // What became of the draft, in the app's words (:285-306): a written draft replaces the text; a photo the model
          // judged not to be evidence keeps the owner's text and says why; anything else is the app's try-again line.
          if (after !== null && after.captionDraft === "written") setCaption(after.caption);
          else if (after !== null && after.captionDraft === "notUsefulEvidence") setNote("photos.sheet.notUseful");
          else setError("photos.sheet.draftFailed");
        }}
      >
        <span className="sparkle" aria-hidden="true">✦</span> {say(drafting ? "photos.sheet.drafting" : "photos.sheet.draft")}
      </button>
      {error !== null ? <Said id={error} as="p" className="sheet-foot error" />
        : note !== null ? <Said id={note} as="p" className="sheet-foot" />
        : <p className="sheet-foot" data-held-copy="photos.sheet.footer">{sayWith("photos.sheet.footer", { exhibit: say("photos.exhibit") })}</p>}
    </Sheet>
  );
}
